import { TrashIcon } from '@heroicons/react/outline';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import {
  refreshWorkflow,
  selectChangeWorkflowNextStepTo,
  setChangeWorkflowNextStepTo,
  setLastCommentsRefresh,
  setLastUpdateProductsPlanDate,
  setWorkflowCurrentStep,
  setWorkflowNextStep,
} from '@store/reducers/contactManagerSlice';
import { formatDate, getOptions, queryParams, openPdf } from '@shared/commonFunctions';
import { GridCell } from '@shared/components/grid/GridCell';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { dateFormatWithTime } from '@shared/constants/formats';
import { Loader } from '@shared/enhancers/withLoader';
import { ContactDetailsSection, ContactDetailsSectionWrapper, Contrast, NameRow } from '@styles/ContactManager/HeaderStyles';
import {
  EmptyWorkflowContainer,
  RowInfo,
  RowName,
  StyledSelect,
  StyledSelectLabel,
  WorkflowContainer,
  WorkflowDetailsContent,
  WorkflowNextSteps,
} from '@styles/ContactManager/WorkflowDetailsStyles';
import { colorTheme } from '@styles/muiTheme';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { ADDFileType, IAsset } from '@modules/ContactManager/components/Tabs/components/Assets/interfaces';
import {
  IVehicleTitleInfoData,
  pullRegistrationInfo,
  requestPullVehicleTitleInfo,
} from '@modules/ContactManager/components/Tabs/shared';
import { IConfiguration } from '@modules/admin/AdminTools/ConfigurationTable/types';
import {
  CommonReasons,
  INextStep,
  ISkipStep,
  IStep,
  IStepReason,
  IWorkflow,
  TabIndexEnum,
  TDealWorkflowDetailsProps,
} from '../../interfaces';
import {
  requestChangeConditions,
  requestChangeReasons,
  requestDeleteWFStep,
  requestMoveStepByDealId,
  requestMoveStepByLeadId,
  useLoadCurrentWorkFlowLead,
} from '../../requests';
import { ReasonsModal, ReasonsValues } from './components/Modal/ReasonsModal';
import { ReasonsPopover } from './components/Modal/ReasonsPopover';

interface IMoveStepOverride {
  override: boolean;
  isConfirmedNo: boolean;
  isConfirmedYes: boolean;
}

interface IReason {
  reasonId?: string;
  description?: string | null;
}

interface IMoveStep {
  currentStepId?: string;
  nextStepId?: string;
  override: IMoveStepOverride;
  reasons: IReason[];
}

enum ButtonNames {
  reasons = 'Reasons',
  conditions = 'Conditions',
}

enum PATH_NAME {
  DEAL_REASONS = 'dealreasons',
  LEAD_REASONS = 'leadreasons',
}

const MAX_WORKFLOW_LIMIT = 2;

export const WorkflowDetails = ({ leadId, dealId, currentTab }: TDealWorkflowDetailsProps) => {
  const AutoSubmitConfigurationGroup = 'AUTO_SUBMIT';
  const AutoSubmitLeadStepToCheck = 'WF_LEAD_STEP_TO_CHECK';
  const [isFirstRender, setIsFirstRender] = useState(true);
  const params = queryParams();
  const { data: assets } = useSWR<IAsset[]>(`/${SERVICES.ASSET}/contacts/${params.id}/assets`);
  const dispatch = useDispatch();
  const refreshWorkflowSection = useSelector(refreshWorkflow);
  const { isAdmin } = useUserAccount();
  const [loading, setLoading] = useState<boolean>(false);
  const [isWFStepDeleteLoading, setWFStepDeleteLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<IStep | null>(null);
  const [reasonModalOpenStatus, handleReasonModalOpenStatus] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentReasons, setCurrentReasons] = useState<IStepReason[]>([]);
  const [matchedReasons, setMatchedReasons] = useState<IStepReason[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<ReasonsValues>();
  const [foundStep, setFoundStep] = useState<INextStep | null>(null);
  const [override, setOverride] = useState({
    override: false,
    isYesNo: false,
    isYesCancel: false,
    isConfirmed: false,
  });
  const [buttonTitle, setButtonTitle] = useState({ buttonLabel1: '', buttonLabel2: '' });
  const [isCheckboxChecked, setCheckboxChecked] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [conditions, setConditions] = useState(false);
  const [stepsToSkip, setStepsToSkip] = useState<number[] | null>([]);
  const [skipStep, setSkipStep] = useState<ISkipStep | null>(null);

  const updateWorkflowNextStep = useSelector(selectChangeWorkflowNextStepTo);

  const open = Boolean(anchorEl);
  const isDealTab = currentTab === TabIndexEnum.Deals;
  const isDeal = isDealTab && !!dealId;
  const isLead = !isDeal;
  const WORKFLOW_CHECK_TO_MOVE_DEAL = 'WORKFLOW_CHECK_TO_MOVE_DEAL';
  const INHOUSE_SKIP_STEPS = 'INHOUSE_SKIP_STEPS';
  const CONFIGURATION_ENDPOINT_LINK = `${SERVICES.CONFIGURATION}/configurations/configuration-group/${WORKFLOW_CHECK_TO_MOVE_DEAL}/token-name/${INHOUSE_SKIP_STEPS}`;

  const { data: configuration, isValidating: isLoadingConfiguration } = useSWR<IConfiguration>(CONFIGURATION_ENDPOINT_LINK);

  const getStepsToSkip = useCallback(() => {
    try {
      if (configuration && !isLoadingConfiguration && configuration.value) {
          const steps = configuration.value.split(',').map((s) =>  Number(s.replace('S', '')));
          return steps;
      }
      return null;
    } catch { return null; }
  }, [configuration, isLoadingConfiguration]);

  useEffect(() => {
    const steps = getStepsToSkip();
    if (steps && steps.length > 0) setStepsToSkip(steps);
  }, [configuration, isLoadingConfiguration]);

  const { data: configurationTables } = useSWR<IConfiguration[]>('/configuration-service/configurations', {
    revalidateOnMount: undefined,
    revalidateIfStale: true,
  });
  const {
    data: currentWorkflowByDealId,
    isLoading: dealWorkflowLoading,
    mutate: mutateWorkflowByDealId,
  } = useSWR<IWorkflow>(isDeal && `${SERVICES.WORKFLOW}/deals/${dealId}/dealworkflow`);

  const { currentWorkflowLead, leadWorkflowLoading, reloadCurrentWorkFlowLead } = useLoadCurrentWorkFlowLead(
    !isDealTab ? leadId : ''
  );

  const { mutate: refreshContact } = useSWR(params?.id && `/${SERVICES.CONTACT}/contacts/${params.id}`, {
    revalidateOnMount: false,
  });

  // Only two workflows need to show.
  // So here if more than two workflows will returned by API then only first two will be used to display.
  const workflow = useMemo(() => {
    let workflowResult;

    if (isDealTab) {
      workflowResult = currentWorkflowByDealId ?? {};
    } else {
      workflowResult = currentWorkflowLead ?? {};
    }

    return {
      ...workflowResult,
      steps:
        (workflowResult?.steps ?? []).length > MAX_WORKFLOW_LIMIT
          ? (workflowResult?.steps ?? []).slice(0, 2)
          : workflowResult?.steps ?? [],
    };
  }, [currentWorkflowByDealId, currentWorkflowLead, isDealTab]);

  const isLoading = useMemo(() => dealWorkflowLoading || leadWorkflowLoading, [dealWorkflowLoading, leadWorkflowLoading]);

  // update next workflow from cudl document upload result
  useEffect(() => {
    const foundStep = workflow?.steps?.[0]?.nextSteps?.find((item: INextStep) => {
      if (item.workflowStepName === updateWorkflowNextStep) {
        return item;
      }
    });

    if (foundStep) {
      handleNextStepsChange(foundStep.workflowStepId, workflow?.steps?.[0]);
    }
  }, [updateWorkflowNextStep, workflow]);

  useEffect(() => {
    if (!isDeal) return;

    if (isFirstRender) {
      setIsFirstRender(false); // Skip the first render
      return;
    }

    if (refreshWorkflowSection) {
      mutateWorkflowByDealId();
    }
  }, [refreshWorkflowSection, isDeal, isFirstRender]);

  // setting STEP
  useEffect(() => {
    if (workflow?.steps.length) {
      const currentStepDetails = workflow?.steps[0];
      setCurrentStep(currentStepDetails);
      dispatch(
        setWorkflowCurrentStep({
          workflowName: currentStepDetails?.workflowName,
          workflowStepName: currentStepDetails?.workflowStepName,
        })
      );
    } else {
      dispatch(setWorkflowCurrentStep(null));
    }
  }, [workflow]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, index = 0, isStepCondition = false) => {
    setConditions(isStepCondition);
    setStepIndex(index);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const options = useCallback((nextSteps: INextStep[]) => {
    return getOptions(nextSteps, 'workflowStepId', 'displayName');
  }, []);

  const getSortedReasons = useCallback((reasons?: CommonReasons[]) => {
    const initialReasonsArr = reasons ?? [];
    const sortByLabel = (i: CommonReasons, j: CommonReasons) => (i.label < j.label ? -1 : i.label > j.label ? 1 : 0);
    const lcConditionsTrueArr = initialReasonsArr.filter((reasonObj) => reasonObj?.isLcCondition).sort(sortByLabel);

    const lcConditionsFalseArr = initialReasonsArr.filter((reasonObj) => !reasonObj?.isLcCondition).sort(sortByLabel);
    return [...lcConditionsTrueArr, ...lcConditionsFalseArr];
  }, []);

  const handleWFStepDelete = useCallback(
    async (workflowStepId: string) => {
      setWFStepDeleteLoading(true);
      await requestDeleteWFStep(dealId, workflowStepId);
      await mutateWorkflowByDealId();
      setWFStepDeleteLoading(false);
    },
    [dealId]
  );

  const handleCheckboxChange = useCallback(
    async (item: CommonReasons, checked: boolean) => {
      setCheckboxChecked(true);
      let id = dealId;
      let path = PATH_NAME.DEAL_REASONS;
      if (currentTab !== TabIndexEnum.Deals) {
        id = workflow?.leadId ?? '';
        path = PATH_NAME.LEAD_REASONS;
      }
      id &&
        (await requestChangeReasons(id, path, {
          workflowStepReasonId: item.id,
          isResolved: !checked,
          isResolvedLc: false,
          isResolvedLp: false,
          parentEntityId: id,
        }).then(async () => {
          path === PATH_NAME.DEAL_REASONS ? await mutateWorkflowByDealId() : await reloadCurrentWorkFlowLead();
        }));
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      setCheckboxChecked(false);
      let reasonsArr = getSortedReasons(workflow?.steps[stepIndex]?.reasons) ?? [];
      reasonsArr = reasonsArr.map((reason) => {
        if (reason.id === item.id) {
          reason.isResolved = !checked;
          return reason;
        }
        return reason;
      });
      if (reasonsArr.every((reason) => reason.isResolved == true)) {
        handleClose();
      }
    },
    [dealId, currentTab, workflow?.leadId]
  );

  const handleResolveConditions = useCallback(
    async (item: CommonReasons, checked: boolean, isLC = false, isLP = false, isConditionLp = false) => {
      setCheckboxChecked(true);
      await requestChangeConditions(dealId, isConditionLp, {
        workflowStepReasonId: item.id,
        isResolved: false,
        isResolvedLc: isLC,
        isResolvedLp: isLP,
        parentEntityId: dealId,
      });
      await mutateWorkflowByDealId();
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      setCheckboxChecked(false);
      let reasonsArr = getSortedReasons(workflow?.steps[stepIndex]?.reasons) ?? [];
      reasonsArr = reasonsArr.map((reason) => {
        if (reason.id === item.id) {
          reason.isResolvedLc = isLC;
          reason.isResolvedLp = isLP;
          return reason;
        }
        return reason;
      });
      if (reasonsArr.every((reason) => reason.isResolvedLc == true)) {
        handleClose();
      }
    },
    [dealId]
  );

  const handleCloseReasonModal = useCallback(() => {
    handleReasonModalOpenStatus(0);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processDMVData = (data: any) => {
    const { accessTokenDoesNotExists, authorizeUrl, isError, errorDescription, fileInBytes } = data;
    if (accessTokenDoesNotExists) {
      window.open(authorizeUrl, '_new');
    } else if (isError) {
      Notistack.generalError(errorDescription);
    } else {
      openPdf(fileInBytes);
    }
  };

  const handleSubmit = useCallback(
    async (payload: IMoveStep, nextStep?: INextStep | null) => {
      if (!isDeal) {
        const leadConfig = configurationTables?.find(
          (r) => r.configurationGroup == AutoSubmitConfigurationGroup && r.tokenName == AutoSubmitLeadStepToCheck
        );
        if (leadConfig?.value == nextStep?.workflowStepNumericId?.toString()) {
          Notistack.infoMessage(
            'Application is being considered for Auto Submit, this may take a few seconds longer than normal.'
          );
        }
      }

      const rawResp = isDeal
        ? await requestMoveStepByDealId(dealId, payload)
        : await requestMoveStepByLeadId(workflow?.leadId || '', payload);

      //  const assets = useSelector(allAssets);
      const resp = rawResp?.data;
      if (resp) {
        if (resp.isDMVAutoPull && resp.assetIdForDMVPull) {
          const asset = assets?.find((a) => a.id === resp.assetIdForDMVPull);
          if (asset) {
            const postData: IVehicleTitleInfoData = {
              stateCode: '',
              userId: '',
              vehicleServiceType: ADDFileType.Pdf,
              caseNumber: '',
              make: asset.make,
              year: asset.year?.toString() ?? '',
              requesterCode: '',
              ncicCode: '',
            };
            const titleInfo = await requestPullVehicleTitleInfo(asset.vin, asset.id, postData);
            processDMVData(titleInfo);
            const regInfo = await pullRegistrationInfo(asset.vin, asset.id, dealId, postData);
            processDMVData(regInfo);
          }
        }
        if (resp?.message) {
          setOverride({
            override: resp.isOverride,
            isYesNo: resp.isYesNo,
            isYesCancel: resp.isYesCancel,
            isConfirmed: resp.isConfirmed,
          });
          setButtonTitle({
            buttonLabel1: resp.buttonLabel1,
            buttonLabel2: resp.buttonLabel2
          });
          setErrorMessage(resp?.message || '');
        } else {
          if (isDeal) {
            mutateWorkflowByDealId();
          } else {
            if (isLead) {
              await reloadCurrentWorkFlowLead();
            }
          }
          dispatch(setWorkflowNextStep(nextStep as INextStep));
          dispatch(setLastUpdateProductsPlanDate(new Date().toISOString()));
          dispatch(setLastCommentsRefresh(new Date().toISOString()));
          refreshContact();
          // this found step must have the configured reason lender mapping, which was saved in workflow step.
          setCurrentStep({ ...nextStep } as IStep);
          if (resp?.autoSubmitMessage) {
            setOverride({
              override: false,
              isYesNo: false,
              isYesCancel: false,
              isConfirmed: false,
            });
            setButtonTitle({
            buttonLabel1: resp.buttonLabel1,
            buttonLabel2: resp.buttonLabel2
          });
            setErrorMessage(resp?.autoSubmitMessage || '');
          } else {
            handleCloseReasonModal();
            setErrorMessage('');
          }
        }
      }
    },
    [isDeal, workflow?.leadId, dealId]
  );

  const handleSubmitReasonModal = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (reasons?: any, nextStep?: INextStep | null | undefined, step?: IStep, overridePayload?: IMoveStepOverride) => {
      setLoading(true);
      setSelectedReasons(reasons);
      nextStep = nextStep?.reasons ? nextStep : foundStep;
      const keys = reasons ? Object.keys(reasons) : [];
      const mappedReasons = [];
      for (const key of keys) {
        if (reasons && reasons[key as string][0] === true) {
          mappedReasons.push({
            reasonId: key,
            description: reasons[key as string][1].toLowerCase()?.trim() === 'other' ? reasons.description : null,
          });
        }
      }
      const defaultOverride = {
        override: false,
        isConfirmedYes: false,
        isConfirmedNo: false,
      };
      const payload = {
        leadId,
        currentStepId: step?.workflowStepId || currentStep?.workflowStepId,
        nextStepId: nextStep?.workflowStepId,
        override: overridePayload ? overridePayload : defaultOverride,
        reasons: mappedReasons || [],
      };
      await handleSubmit(payload, nextStep);
      setLoading(false);
    },
    [currentStep, override, foundStep, leadId, handleSubmit, buttonTitle]
  );

  const handleNextStepsChange = useCallback(
    async (value: string, step: IStep) => {
      if (value) {
        const foundStep = step?.nextSteps.find((item: INextStep) => {
          if (item.workflowStepId === value) {
            return item;
          }
        });
        if (foundStep && stepsToSkip?.includes(foundStep.workflowStepNumericId))
        {
          const skipStep: ISkipStep = {
            workflowStepName: foundStep.displayName,
            workFlowStepMessage: `Workflow step "${foundStep.displayName}" will be deprecated soon, please select another step`
          };
          setSkipStep(skipStep);
          return;
        }
        setFoundStep(foundStep as INextStep);
        setCurrentReasons(foundStep?.reasons || []);
        setMatchedReasons({ ...foundStep, nextSteps: step?.nextSteps }?.reasons || []);
        setCurrentStep(step);

        if (foundStep?.reasons.length) {
          handleReasonModalOpenStatus((prevState) => prevState + 1);
        } else {
          await handleSubmitReasonModal([] as unknown as ReasonsValues, foundStep as INextStep, step);
        }
        dispatch(setChangeWorkflowNextStepTo(null));
      }
    },
    [handleSubmitReasonModal]
  );

  const handleConfirmAlert = useCallback(
    (isOverride = false, isConfirmed = false, isDirectClose = false, confirmed = false) => {
      const payload = {
        override: isOverride,
        isConfirmedYes: isOverride ? false : isConfirmed,
        isConfirmedNo: isOverride ? false : !isConfirmed,
        confirmed: confirmed,
      };
      if ((override.override || override.isYesNo || override.isYesCancel || override.isConfirmed) && !isDirectClose) {
        handleSubmitReasonModal(selectedReasons as unknown as ReasonsValues, {} as INextStep, {} as IStep, payload);
      } else {
        !loading && setErrorMessage('');
      }
    },
    [override, loading, selectedReasons, handleSubmitReasonModal]
  );

  const isFullResolved = useCallback((reasons: CommonReasons[], isStepCondition = false) => {
    const isResolved = reasons.find((reason) =>
      isStepCondition ? !reason.isResolvedLc || !reason.isResolvedLp : !reason.isResolved
    );

    return !isResolved;
  }, []);

  return (
    <WorkflowDetailsContent isDeal={isDealTab}>
      <ContactDetailsSectionWrapper style={{ marginRight: '0px' }}>
        <LoaderBackdrop open={loading} />
        <ContactDetailsSection style={{ paddingRight: '0px' }}>
          <Stack
            flexDirection="row"
            height="100%"
            gap={2}
          >
            {!isCheckboxChecked && (isWFStepDeleteLoading || isLoading) ? (
              <Loader
                color="inherit"
                minHeight="160px"
              />
            ) : (
              <>
                {workflow.steps?.length ? (
                  workflow.steps.map((value, index) => (
                    <WorkflowContainer
                      key={value.workflowStepId}
                      flexDirection="column"
                      length={workflow?.steps.length}
                      index={index}
                    >
                      <NameRow style={{ justifyContent: 'space-between' }}>
                        <GridCell
                          variant="h4"
                          text={value?.workflowStepName || ''}
                          sx={{ maxWidth: `${(workflow.steps ?? []).length > 1 ? '300px' : '500px'}` }}
                        />
                        {isAdmin && isDeal && (workflow.steps ?? []).length > 1 && (
                          <IconButton
                            sx={{ padding: 0 }}
                            onClick={() => handleWFStepDelete(value.workflowStepId)}
                          >
                            <TrashIcon
                              color={colorTheme.palette.common.white}
                              height="16px"
                            />
                          </IconButton>
                        )}
                      </NameRow>
                      <Stack
                        flexDirection="column"
                        gap="5px"
                        mb={1}
                      >
                        {isDeal && (
                          <Stack flexDirection="row">
                            <RowName>WF:</RowName>
                            <GridCell
                              text={value?.workflowName || ''}
                              sx={{ maxWidth: '200px', fontWeight: 700, letterSpacing: 0 }}
                            />
                          </Stack>
                        )}

                        {((!isDeal && !isLead) || (isLead && leadId) || isDeal) && (
                          <>
                            <Stack flexDirection="row">
                              <RowName>Set:</RowName>
                              <RowInfo>{formatDate(value?.createdAt, { format: dateFormatWithTime })}</RowInfo>
                            </Stack>

                            <Stack flexDirection="row">
                              <RowName>Days:</RowName>
                              <RowInfo>{value?.days || ''}</RowInfo>
                            </Stack>
                          </>
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        gap={1}
                      >
                        {((!isDeal && !isLead) || (isLead && leadId) || isDeal) && value?.nextSteps?.length && (
                          <Box minWidth="185px">
                            <WorkflowNextSteps>
                              <StyledSelect
                                hideNoneOption
                                disabled={Boolean(!value?.showNextStep)}
                                value={String(value?.workflowStepId)}
                                options={options(value?.nextSteps)}
                                renderValue={() => <StyledSelectLabel>Next steps</StyledSelectLabel>}
                                onChange={(selectedValue: string) =>
                                  handleNextStepsChange(selectedValue, value as unknown as IStep)
                                }
                              />
                            </WorkflowNextSteps>
                          </Box>
                        )}
                        <Box
                          display="flex"
                          alignItems="center"
                          pl={1}
                        >
                          {value?.reasons.length ? (
                            <Contrast
                              bold
                              isFullResolved={isFullResolved(value?.reasons ?? [], value.isStepCondition)}
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleClick(e, index, value.isStepCondition)}
                            >
                              {value.isStepCondition ? ButtonNames.conditions : ButtonNames.reasons}
                            </Contrast>
                          ) : (
                            ''
                          )}
                        </Box>
                      </Stack>
                    </WorkflowContainer>
                  ))
                ) : (
                  <EmptyWorkflowContainer>
                    <Typography variant="h3">{`No ${isDealTab ? 'Deal' : 'Lead'} is selected`}</Typography>
                  </EmptyWorkflowContainer>
                )}
              </>
            )}
            {!isDealTab && !isLoading && (
              <Typography
                display="block"
                flex="1 1 180px"
              >
                {currentStep?.description || ''}
              </Typography>
            )}
          </Stack>

          {!!workflow?.steps[stepIndex]?.reasons.length && (
            <ReasonsPopover
              open={open}
              anchorEl={anchorEl}
              conditions={conditions}
              handleClose={handleClose}
              reasons={getSortedReasons(workflow?.steps[stepIndex]?.reasons)}
              handleCheckboxChange={handleCheckboxChange}
              handleResolveConditions={handleResolveConditions}
              loading={isCheckboxChecked || isLoading}
            />
          )}
        </ContactDetailsSection>
      </ContactDetailsSectionWrapper>
      {skipStep && (
        <AlertModal
          open
          title="Alert"
          subtitle={skipStep.workFlowStepMessage}
          buttonText="Ok"
          hideCancel={true}
          onConfirm={() => setSkipStep(null)}
        /> 
      )}    
      {errorMessage && override.override && !override.isYesNo && !override.isYesCancel && (
        <AlertModal
          title="Override Workflow"
          maxWidth="650px"
          subtitle={errorMessage}
          open={override.override}
          isLoading={loading}
          buttonText="Override"
          cancelButtonText="Ok"
          onConfirm={() => handleConfirmAlert(true, false, false)}
          onClose={() => !loading && setErrorMessage('')}
        />
      )}
      {errorMessage &&
        (override.isYesNo ||
          override.isYesCancel ||
          (!override.isYesNo && !override.isYesCancel && !override.override && !override.isConfirmed)) && (
          <AlertModal
            title="Alert"
            maxWidth="650px"
            subtitle={errorMessage}
            open={override.isYesNo || override.isYesCancel || (!override.isYesNo && !override.isYesCancel && !override.override)}
            isLoading={loading}
            hideCancel={!override.override && !override.isYesNo && !override.isYesCancel}
            buttonText={!override.override && !override.isYesNo &&  !buttonTitle.buttonLabel1? (!override.isYesCancel ? 'Ok' : 'Yes') : buttonTitle.buttonLabel1}
            cancelButtonText={!buttonTitle.buttonLabel2? (override.isYesCancel ? 'Cancel' : 'No') : buttonTitle.buttonLabel2}
            onConfirm={() => handleConfirmAlert(false, true, false)}
            onClose={() => handleConfirmAlert(false, false, override.isYesCancel)}
          />
        )}
      {errorMessage && override.isConfirmed && (
        <AlertModal
          title="Alert"
          maxWidth="500px"
          subtitle={errorMessage}
          open={override.isConfirmed}
          isLoading={loading}
          buttonText="Ok"
          onConfirm={() => handleConfirmAlert(false, false, false, true)}
          onClose={() => !loading && setErrorMessage('')}
        />
      )}
      {!!reasonModalOpenStatus && (
        <ReasonsModal
          reasonModalPosition={reasonModalOpenStatus}
          handleClose={handleCloseReasonModal}
          handleSubmit={handleSubmitReasonModal}
          currentReasons={currentReasons}
          matchedReasons={matchedReasons}
          loading={loading}
        />
      )}
    </WorkflowDetailsContent>
  );
};
